import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { CampoData, Formulario, Rotulo, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { dinheiro, mesIsoValido } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { useEquipes } from './FormOportunidade';
import { Kpi } from './Kpi';
import type { Meta } from './tipos';
import { anunciarOcultos } from '../../componentes/sensivel';

/** Mês atual no formato AAAA-MM, no relógio do navegador. */
function mesAtual() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** AAAA-MM para "outubro de 2026" no idioma da interface. */
function mesPorExtenso(mes: string, idioma: string) {
  if (!mesIsoValido(mes)) return mes;
  const locale = idioma === 'en' ? 'en-US' : idioma === 'es' ? 'es-ES' : 'pt-BR';
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(new Date(`${mes}-15T12:00:00`));
}

function Barra({ percentual }: { percentual: number | null }) {
  const p = Math.max(0, Math.min(100, percentual ?? 0));
  return (
    <div className="h-2 rounded-full bg-sup-3" role="presentation">
      <div className={`h-2 rounded-full ${(percentual ?? 0) >= 100 ? 'bg-verde' : 'bg-acento-forte'}`} style={{ width: `${p}%` }} />
    </div>
  );
}

function ModalMeta({ mes, atual, aoFechar, aoSalvar }: { mes: string; atual: Meta | null; aoFechar: () => void; aoSalvar: () => void }) {
  const { t, i18n } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const equipes = useEquipes();
  const campos: DefCampo[] = atual
    ? [{ nome: 'valor_centavos', rotulo: `${t('campos.meta')}: ${atual.alvo_nome} (${mesPorExtenso(atual.mes, i18n.language)})`, tipo: 'dinheiro', obrigatorio: true }]
    : [
        { nome: 'alvo', rotulo: t('campos.alvo'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: [{ valor: 'equipe', rotulo: t('metas.equipe') }, { valor: 'vendedor', rotulo: t('metas.vendedor') }] },
        {
          nome: 'equipe_id',
          rotulo: t('campos.equipe'),
          tipo: 'selecao',
          obrigatorio: true,
          largura: 'meia',
          visivel: (v) => v.alvo === 'equipe',
          opcoes: (equipes.data?.itens ?? []).map((e) => ({ valor: e.id, rotulo: e.nome })),
        },
        { nome: 'usuario_id', rotulo: t('campos.vendedor'), tipo: 'busca', obrigatorio: true, largura: 'meia', visivel: (v) => v.alvo === 'vendedor', busca: { caminho: '/membros', rotulo: (i) => String(i.nome) } },
        { nome: 'valor_centavos', rotulo: t('campos.meta'), tipo: 'dinheiro', obrigatorio: true },
      ];
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={`${t('modal.metaTitulo')} ${mesPorExtenso(atual?.mes ?? mes, i18n.language)}`}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-meta">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <Formulario
        id="form-meta"
        campos={campos}
        inicial={atual ? { valor_centavos: atual.valor_centavos } : { alvo: 'equipe' }}
        aoEnviar={async (v) => {
          if (atual) await patch(`/pipeline/metas/${atual.id}`, { valor_centavos: v.valor_centavos });
          else await post('/pipeline/metas', { mes, valor_centavos: v.valor_centavos, ...(v.alvo === 'equipe' ? { equipe_id: v.equipe_id } : { usuario_id: v.usuario_id }) });
          toast.sucesso(tn('comum.salvo'));
          aoSalvar();
        }}
      />
    </Modal>
  );
}

function Grupo({ titulo, itens, aoEditar, aoExcluir }: { titulo: string; itens: Meta[]; aoEditar: (m: Meta) => void; aoExcluir: (m: Meta) => void }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  if (!itens.length) return null;
  return (
    <Cartao titulo={titulo}>
      <ul className="flex flex-col gap-5">
        {itens.map((m) => (
          <li key={m.id} className="min-w-0">
            <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <span className="w-full min-w-0 truncate text-sm font-medium sm:w-auto sm:flex-1" title={m.alvo_nome}>{m.alvo_nome}</span>
              <span className="flex min-w-0 max-w-full items-center gap-1">
                <span className="numero min-w-0 text-sm break-words text-tinta-2">
                  {anunciarOcultos(dinheiro(m.realizado_centavos, m.moeda))} / {anunciarOcultos(dinheiro(m.valor_centavos, m.moeda))}
                </span>
                {s.pode('pipeline.metas') && <Botao tamanho="sm" variante="fantasma" className="shrink-0" aria-label={tn('comum.editar')} onClick={() => aoEditar(m)} icone={<Pencil className="size-4" />} />}
                {s.pode('pipeline.metas') && <Botao tamanho="sm" variante="fantasma" className="shrink-0" aria-label={tn('comum.excluir')} onClick={() => aoExcluir(m)} icone={<Trash2 className="size-4" />} />}
              </span>
            </div>
            <Barra percentual={m.percentual} />
            <p className="numero mt-1 text-xs text-tinta-3">{m.percentual === null ? t('metas.semMeta') : t('metas.percentual', { n: m.percentual })}</p>
          </li>
        ))}
      </ul>
    </Cartao>
  );
}

/** M2.4 aba Metas: meta x realizado do mês, por equipe e por vendedor. Realizado = valor das oportunidades ganhas no mês. */
// Val5 (29/10): metas e equipes têm ação própria (`pipeline.metas`).
export function Metas() {
  const { t, i18n } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [mes, setMes] = useState(mesAtual());
  const [editando, setEditando] = useState<Meta | 'nova' | null>(null);
  const [excluindo, setExcluindo] = useState<Meta | null>(null);
  const q = useQuery({ queryKey: ['pipeline', 'metas', mes], queryFn: () => get<{ itens: Meta[]; mes: string }>('/pipeline/metas', { mes }), enabled: /^\d{4}-\d{2}$/.test(mes) });
  const recarregar = () => qc.invalidateQueries({ queryKey: ['pipeline', 'metas'] });
  const excluir = useMutation({
    mutationFn: (id: string) => del(`/pipeline/metas/${id}`),
    onSuccess: async () => {
      setExcluindo(null);
      toast.sucesso(tn('comum.removido'));
      await recarregar();
    },
    onError: (e) => {
      setExcluindo(null);
      toast.erro(mensagemDeErro(e));
    },
  });
  return (
    <>
      {s.pode('pipeline.metas') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setEditando('nova')}>
            {t('acoes.novaMeta')}
          </Botao>
        </AcaoPrimaria>
      )}
      <div className="mb-4 w-48">
        <Rotulo id="mes-metas" rotulo={t('campos.mes')}>
          <CampoData id="mes-metas" type="month" value={mes} onChange={(e) => e.target.value && setMes(e.target.value)} />
        </Rotulo>
      </div>
      <Estado consulta={q}>
        {(d) => {
          if (d.itens.length === 0) {
            return (
              <Cartao semPadding>
                <Vazio texto={t('metas.vazio')} />
              </Cartao>
            );
          }
          const equipes = d.itens.filter((m) => m.alvo === 'equipe');
          const vendedores = d.itens.filter((m) => m.alvo === 'vendedor');
          const metaEquipes = equipes.reduce((a, m) => a + m.valor_centavos, 0);
          const realizadoEquipes = equipes.reduce((a, m) => a + m.realizado_centavos, 0);
          return (
            <div className="flex flex-col gap-4">
              {equipes.length > 0 && <Kpi rotulo={`${t('metas.total')} (${t('metas.equipe')})`} valor={`${dinheiro(realizadoEquipes)} / ${dinheiro(metaEquipes)}`} cor={metaEquipes > 0 && realizadoEquipes >= metaEquipes ? 'verde' : 'tinta-1'} />}
              <div className="grid gap-4 lg:grid-cols-2">
                <Grupo titulo={t('metas.equipe')} itens={equipes} aoEditar={setEditando} aoExcluir={setExcluindo} />
                <Grupo titulo={t('metas.vendedor')} itens={vendedores} aoEditar={setEditando} aoExcluir={setExcluindo} />
              </div>
            </div>
          );
        }}
      </Estado>
      {editando && (
        <ModalMeta
          key={editando === 'nova' ? 'nova' : editando.id}
          mes={mes}
          atual={editando === 'nova' ? null : editando}
          aoFechar={() => setEditando(null)}
          aoSalvar={async () => {
            setEditando(null);
            await recarregar();
          }}
        />
      )}
      <Confirmar
        aberto={!!excluindo}
        aoFechar={() => setExcluindo(null)}
        aoConfirmar={() => excluindo && excluir.mutate(excluindo.id)}
        titulo={t('confirmar.excluir')}
        texto={excluindo ? `${excluindo.alvo_nome} (${mesPorExtenso(excluindo.mes, i18n.language)})` : ''}
        perigoso
        carregando={excluir.isPending}
      />
    </>
  );
}
